/**
 * Pairing an authenticator app: show the QR, take one code back.
 *
 * The code is what proves the app really holds the secret — until it arrives,
 * the server keeps the enrolment pending, so abandoning this screen leaves the
 * account exactly as it was rather than locking anyone out.
 */
import { useEffect, useState } from 'react';
import QRCode from 'qrcode';
import { authApi } from '../../authApi.js';
import { ErrorBanner, SubmitButton } from '../FormBits.js';
import { bigButton, codeInput, Stepper } from './loginBits.js';

export function TotpEnrollStep({
  error,
  onConfirm,
  onFailure,
}: {
  error: string | null;
  onConfirm: (code: string) => Promise<void>;
  onFailure: (err: unknown) => void;
}) {
  const [secret, setSecret] = useState<string | null>(null);
  const [qr, setQr] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    authApi
      .beginEnrollment()
      .then(async ({ otpauthUri }) => {
        if (cancelled) return;
        setSecret(new URL(otpauthUri.replace('otpauth://', 'https://')).searchParams.get('secret'));
        setQr(await QRCode.toDataURL(otpauthUri, { margin: 1, width: 240 }));
      })
      .catch(onFailure);
    return () => {
      cancelled = true;
    };
  }, []);

  if (!qr) return null;

  return (
    <form
      onSubmit={async (e) => {
        e.preventDefault();
        setBusy(true);
        await onConfirm(code.trim());
        setBusy(false);
        setCode('');
      }}
    >
      <Stepper current={2} />
      <h2 className="mb-1 text-2xl font-bold">סרקו את הקוד</h2>
      <p className="mb-5 text-base text-white/70">פתחו את אפליקציית האימות בטלפון וסרקו.</p>
      {/* w-fit so the white card hugs the QR — a full-width card put far more
          white to the left and right of the code than above and below it. */}
      <div className="mx-auto mb-5 w-fit rounded-xl bg-white p-4 leading-none">
        <img src={qr} alt="קוד QR להגדרת אפליקציית האימות" width={240} height={240} />
      </div>
      <label className="mb-2 block text-base" htmlFor="enroll-code">
        הקוד מהאפליקציה
      </label>
      <input
        id="enroll-code" dir="ltr" required autoFocus inputMode="numeric" pattern="[0-9]{6}" maxLength={6}
        autoComplete="one-time-code" placeholder="000000"
        className={codeInput} value={code} onChange={(e) => setCode(e.target.value)}
      />
      <SubmitButton busy={busy} busyLabel="מסיים…" className={bigButton}>
        סיום
      </SubmitButton>
      {/* Secondary path, tucked away so it cannot distract from the main task. */}
      {secret && (
        <details className="mt-4">
          <summary className="cursor-pointer text-sm text-white/60">לא מצליחים לסרוק?</summary>
          <p className="mt-2 break-all text-center text-sm text-white/70" dir="ltr">
            {secret}
          </p>
          <p className="mt-1 text-center text-xs text-white/50">הקלידו את המפתח הזה באפליקציה במקום לסרוק.</p>
        </details>
      )}
      <ErrorBanner error={error} title="לא הצלחנו להמשיך" />
    </form>
  );
}
